import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { Steps, Card, Form, Button, Space, message, Typography, Select } from 'antd';
import { SaveOutlined, ArrowRightOutlined, ArrowLeftOutlined, SettingOutlined } from '@ant-design/icons';
import PageHeader from '../components/PageHeader';
import OrganisationInfo from '../components/onboarding/OrganisationInfo';
import CrisisManagementTeam from '../components/onboarding/CrisisManagementTeam';
import RegulatoryRules from '../components/onboarding/RegulatoryRules';
import NotificationPreferences from '../components/onboarding/NotificationPreferences';
import TemplatesAndDocuments from '../components/onboarding/TemplatesAndDocuments';
import SiteUpload from '../components/onboarding/SiteUpload';
import MultiCMTConfiguration from '../components/onboarding/MultiCMTConfiguration';
import { getDoc, doc, setDoc, updateDoc, getDocs, collection, addDoc, deleteDoc, query, where } from 'firebase/firestore';
import { logActivity, ACTIVITY_TYPES } from '../utils/activityLogger';
import setupOrganisationProducts from '../utils/setupOrganisationProducts.js';
import cmtService from '../services/cmtService';

const { Step } = Steps;
const { Title } = Typography;
const { Option } = Select;

/**
 * Creates CMT documents in the cmts subcollection from hierarchy data
 */
const createCMTsFromHierarchy = async (orgId, hierarchyData, currentUser) => {
  if (!hierarchyData || !hierarchyData.root) {
    console.warn('No hierarchy data to create CMTs from');
    return;
  }

  const cmtsToCreate = [];
  
  // Recursive function to extract all CMTs from hierarchy
  const extractCMTs = (node, parentId = null, level = 0) => {
    // Map hierarchy level to proper CMT type
    let cmtType = 'primary'; // Default fallback
    if (level === 0) {
      cmtType = 'global';
    } else if (node.name?.toLowerCase().includes('regional')) {
      cmtType = 'regional';
    } else if (node.name?.toLowerCase().includes('functional')) {
      cmtType = 'functional';
    } else if (node.name?.toLowerCase().includes('division')) {
      cmtType = 'division';
    } else {
      cmtType = 'primary'; // For country, site, or other levels
    }

    const cmtData = {
      id: node.id || `cmt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: node.name,
      type: cmtType,
      scope: node.scope || '',
      parentCmtId: parentId,
      isGlobalCmt: level === 0,
      level: level,
      childCmtIds: [],
      createdBy: (currentUser && currentUser.uid) ? currentUser.uid : 'onboarding_system',
      permissions: {
        canViewAllCMTs: level === 0, // Global CMT sees all
        canCoordinateAcrossCMTs: level === 0,
        canEscalateToParent: parentId !== null
      },
      metadata: {
        createdAt: new Date(),
        hierarchyPosition: level
      }
    };
    
    cmtsToCreate.push(cmtData);
    
    // Process children
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(child => {
        // Ensure child has consistent ID that will be used in both parent and child
        if (!child.id) {
          child.id = `cmt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        }
        cmtData.childCmtIds.push(child.id);
        extractCMTs(child, cmtData.id, level + 1);
      });
    }
  };
  
  // Extract all CMTs starting from root
  extractCMTs(hierarchyData.root);
  
  // Sort CMTs by level to ensure parents are created before children
  cmtsToCreate.sort((a, b) => a.level - b.level);
  
  // Create all CMT documents in correct order
  for (const cmtData of cmtsToCreate) {
    try {
      await cmtService.createCMT(orgId, cmtData);
      console.log(`Created CMT: ${cmtData.name} (${cmtData.type}) at level ${cmtData.level}`);
    } catch (error) {
      console.error(`Error creating CMT ${cmtData.name}:`, error);
      throw error;
    }
  }
  
  console.log(`Successfully created ${cmtsToCreate.length} CMTs for organization ${orgId}`);
};

/**
 * 🚨 PHASE 2: CREATE USER DOCUMENTS FROM CMT MEMBER DATA
 * Comprehensive user creation with full error handling and idempotency
 * 
 * @param {string} orgId - Organization ID
 * @param {Object} formValues - Form values from onboarding wizard
 * @param {Object} currentUser - Current authenticated user
 * @returns {Promise<Object>} Results summary
 */
const createUsersFromCMTMembers = async (orgId, formValues, currentUser) => {
  const startTime = Date.now();
  console.log('🔧 USER CREATION: Starting comprehensive user creation', {
    orgId,
    timestamp: new Date().toISOString()
  });

  // 🚨 STEP 1: NORMALIZE MEMBER DATA FROM FORM VALUES
  const allMembers = [];
  
  try {
    // Handle single CMT structure
    if (formValues.cmtMembers && Array.isArray(formValues.cmtMembers)) {
      console.log('📊 SINGLE CMT: Processing single CMT member data', {
        memberCount: formValues.cmtMembers.length
      });
      
      // For single CMT, we need to determine the CMT ID
      // This should be the first (and only) CMT created for this org
      let singleCmtId = null;
      
      // Try to find the CMT ID from hierarchy data or use default
      if (formValues.hierarchyData && formValues.hierarchyData.root) {
        singleCmtId = formValues.hierarchyData.root.id;
      } else {
        // Fallback: query the organization's CMTs to find the default one
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions(undefined, 'australia-southeast2');
        
        try {
          // We'll create a simple query to get CMTs - for now use a known pattern
          singleCmtId = `${orgId}_primary_cmt`; // This should match CMT creation pattern
          console.log('🔍 SINGLE CMT: Using inferred CMT ID', { singleCmtId });
        } catch (cmtQueryError) {
          console.error('❌ SINGLE CMT: Failed to determine CMT ID', cmtQueryError);
          throw new Error('Failed to determine CMT ID for single CMT structure');
        }
      }
      
      formValues.cmtMembers.forEach((member, index) => {
        if (member.email && member.email.trim()) {
          allMembers.push({
            email: member.email.trim().toLowerCase(),
            firstName: member.firstName?.trim() || member.fullName?.split(' ')[0] || 'Unknown',
            lastName: member.lastName?.trim() || member.fullName?.split(' ').slice(1).join(' ') || 'User',
            phone: member.phone?.trim() || null,
            role: member.role?.trim() || 'other',
            title: member.title?.trim() || null,
            cmtId: singleCmtId,
            originalIndex: index,
            source: 'cmtMembers'
          });
        }
      });
    }
    
    // Handle multi-CMT structure
    if (formValues.multiCmtMembers && typeof formValues.multiCmtMembers === 'object') {
      console.log('📊 MULTI CMT: Processing multi-CMT member data', {
        cmtCount: Object.keys(formValues.multiCmtMembers).length
      });
      
      Object.entries(formValues.multiCmtMembers).forEach(([cmtId, members]) => {
        if (Array.isArray(members)) {
          members.forEach((member, index) => {
            if (member.email && member.email.trim()) {
              allMembers.push({
                email: member.email.trim().toLowerCase(),
                firstName: member.firstName?.trim() || member.fullName?.split(' ')[0] || 'Unknown',
                lastName: member.lastName?.trim() || member.fullName?.split(' ').slice(1).join(' ') || 'User',
                phone: member.phone?.trim() || null,
                role: member.role?.trim() || 'other',
                title: member.title?.trim() || null,
                cmtId: cmtId,
                originalIndex: index,
                source: 'multiCmtMembers'
              });
            }
          });
        }
      });
    }

    console.log('📊 NORMALIZATION: Member data normalized', {
      totalMembers: allMembers.length,
      uniqueEmails: new Set(allMembers.map(m => m.email)).size,
      cmtIds: [...new Set(allMembers.map(m => m.cmtId))]
    });

    if (allMembers.length === 0) {
      console.log('ℹ️ USER CREATION: No members to create, skipping user creation');
      return {
        success: true,
        totalMembers: 0,
        created: 0,
        updated: 0,
        errors: [],
        duration: Date.now() - startTime
      };
    }

    // 🚨 STEP 2: CALL ENHANCED CLOUD FUNCTION FOR EACH MEMBER
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions(undefined, 'australia-southeast2');
    const createUserForOrgV2 = httpsCallable(functions, 'createUserForOrgV2');

    console.log('🔧 USER CREATION: Starting parallel user creation operations', {
      memberCount: allMembers.length
    });

    // Use Promise.allSettled to handle partial failures gracefully
    const userCreationPromises = allMembers.map(async (member, index) => {
      try {
        console.log(`🔧 USER ${index + 1}/${allMembers.length}: Creating user`, {
          email: member.email,
          cmtId: member.cmtId,
          role: member.role
        });

        const result = await createUserForOrgV2({
          orgId: orgId,
          cmtId: member.cmtId,
          profile: {
            email: member.email,
            firstName: member.firstName,
            lastName: member.lastName,
            phone: member.phone,
            role: member.role,
            title: member.title
          },
          invite: true // Send invitation emails
        });

        console.log(`✅ USER ${index + 1}/${allMembers.length}: Success`, {
          email: member.email,
          uid: result.user.uid,
          status: result.status
        });

        return {
          success: true,
          email: member.email,
          uid: result.user.uid,
          status: result.status,
          cmtId: member.cmtId,
          originalIndex: member.originalIndex,
          source: member.source
        };

      } catch (error) {
        console.error(`❌ USER ${index + 1}/${allMembers.length}: Failed`, {
          email: member.email,
          cmtId: member.cmtId,
          error: error.message
        });

        return {
          success: false,
          email: member.email,
          cmtId: member.cmtId,
          error: error.message,
          errorCode: error.code,
          originalIndex: member.originalIndex,
          source: member.source
        };
      }
    });

    // Wait for all user creation operations to complete
    const userResults = await Promise.allSettled(userCreationPromises);

    // 🚨 STEP 3: PROCESS RESULTS AND GENERATE SUMMARY
    const summary = {
      success: true,
      totalMembers: allMembers.length,
      created: 0,
      updated: 0,
      errors: [],
      successfulUsers: [],
      duration: Date.now() - startTime
    };

    userResults.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.success) {
        if (result.value.status === 'created') {
          summary.created++;
        } else if (result.value.status === 'updated') {
          summary.updated++;
        }
        summary.successfulUsers.push(result.value);
      } else {
        const error = result.status === 'rejected' ? result.reason : result.value;
        summary.errors.push({
          email: allMembers[index].email,
          cmtId: allMembers[index].cmtId,
          error: error.error || error.message || 'Unknown error',
          errorCode: error.errorCode || error.code
        });
      }
    });

    // 🚨 STEP 4: LOG COMPREHENSIVE RESULTS
    console.log('📊 USER CREATION SUMMARY:', {
      totalMembers: summary.totalMembers,
      created: summary.created,
      updated: summary.updated,
      errors: summary.errors.length,
      successRate: `${Math.round(((summary.created + summary.updated) / summary.totalMembers) * 100)}%`,
      duration: summary.duration
    });

    if (summary.errors.length > 0) {
      console.warn('⚠️ USER CREATION ERRORS:', summary.errors);
      // Set overall success to false if more than 50% failed
      summary.success = summary.errors.length <= (summary.totalMembers / 2);
    }

    return summary;

  } catch (error) {
    console.error('❌ USER CREATION: Fatal error during user creation process', {
      error: error.message,
      duration: Date.now() - startTime
    });

    return {
      success: false,
      totalMembers: allMembers.length,
      created: 0,
      updated: 0,
      errors: [{
        email: 'unknown',
        cmtId: 'unknown',
        error: error.message,
        errorCode: error.code
      }],
      successfulUsers: [],
      duration: Date.now() - startTime
    };
  }
};

export default function OnboardingWizard() {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [isNewOrg, setIsNewOrg] = useState(false);
  const [allFormValues, setAllFormValues] = useState({});
  const [showResetOnboarding, setShowResetOnboarding] = useState(false);
  const [tempOrgId, setTempOrgId] = useState(null);

  // Always sync selectedOrgId from localStorage on mount and whenever it changes
  useEffect(() => {
    const recoveredOrgId = localStorage.getItem('pendingNewOrgId');
    if (!selectedOrgId && recoveredOrgId) {
      setSelectedOrgId(recoveredOrgId);
      console.log('[Onboarding] useEffect: recovered selectedOrgId from localStorage:', recoveredOrgId);
    }
  }, [selectedOrgId]);

  // Access control check
  useEffect(() => {
    console.log('OnboardingWizard: Checking access control');
    console.log('Current user:', currentUser);
    console.log('Navigation state:', location.state);

    if (!currentUser) {
      console.log('No current user, redirecting to login');
      navigate('/login');
      return;
    }

    // Allow platform admins (role: 'admin'), org admins, and developers
    if (currentUser.role !== 'admin' && currentUser.role !== 'org_admin' && currentUser.role !== 'developer' && !currentUser.isAdmin) {
      console.log('User does not have permission:', currentUser);
      message.error("You don't have permission to access the onboarding wizard.");
      navigate('/dashboard');
      return;
    }

    console.log('User has permission to access onboarding wizard');

    // If we have navigation state, use it to set the selected org
    if (location.state?.orgId) {
      console.log('Setting selected org from navigation state:', location.state.orgId);
      setSelectedOrgId(location.state.orgId);
      form.setFieldsValue({ selectedOrgId: location.state.orgId });
      setIsNewOrg(false);
    } else if (location.state?.isNewOrg) {
      setIsNewOrg(true);
    }

    // Check if onboarding is already complete (only for org admins)
    const checkOnboardingStatus = async () => {
      if (currentUser.role !== 'admin' && currentUser.role !== 'developer') {  // Skip check for both platform admins and developers
        if (!currentUser.orgId) {
          console.error('OnboardingWizard: currentUser.orgId is null or undefined!');
          return;
        }
        try {
          console.log('Checking onboarding status for org:', currentUser.orgId);
          const orgDoc = await getDoc(doc(db, 'c3_organisations', currentUser.orgId));
          if (orgDoc.exists() && orgDoc.data().onboardingComplete) {
            console.log('Onboarding already complete, redirecting to dashboard');
            message.info('Onboarding has already been completed.');
            navigate('/dashboard');
          } else {
            console.log('Onboarding not complete, proceeding with wizard');
          }
        } catch (error) {
          console.error('Error checking onboarding status:', error);
          message.error('Failed to verify onboarding status.');
        }
      } else {
        console.log('Skipping onboarding status check for platform admin/developer');
      }
    };

    checkOnboardingStatus();
  }, [currentUser, navigate, db, location.state, form]);

  // Fetch organisations for platform admins and developers
  useEffect(() => {
    const fetchOrganisations = async () => {
      if (currentUser?.role === 'admin' || currentUser?.role === 'org_admin' || currentUser?.role === 'developer' || currentUser?.isAdmin) {
        try {
          console.log('Fetching organisations for user:', currentUser);
          const orgsRef = collection(db, 'c3_organisations');
          const orgsSnapshot = await getDocs(orgsRef);
          const orgsList = orgsSnapshot.docs.map(doc => {
            const data = doc.data();
            console.log('Organisation data:', { id: doc.id, ...data });
            return {
              id: doc.id,
              ...data
            };
          });
                  console.log('Total organisations fetched:', orgsList.length);
        console.log('Organisations list:', JSON.stringify(orgsList, null, 2));
          setOrganisations(orgsList);
        } catch (error) {
          console.error('Error fetching organisations:', error);
          message.error('Failed to load organisations');
        }
      } else {
        console.log('User does not have permission to fetch organisations:', currentUser);
      }
    };

    fetchOrganisations();
  }, [currentUser, db]);

  // Initialize form with default values
  useEffect(() => {
    if (currentUser) {
      if (currentUser.isAdmin || currentUser.role === 'developer') {
        // For platform admins and developers, initialize with empty values
        form.setFieldsValue({
          selectedOrgId: null
        });
      } else {
        // For org admins, initialize with their org ID
        form.setFieldsValue({
          selectedOrgId: currentUser.orgId
        });
        setSelectedOrgId(currentUser.orgId);
      }
    }
  }, [currentUser, form]);

  // On mount, check for a pending new org ID in localStorage
  useEffect(() => {
    const pendingOrgId = localStorage.getItem('pendingNewOrgId');
    if (pendingOrgId && !selectedOrgId) {
      setSelectedOrgId(pendingOrgId);
      console.log('[Onboarding] Restored pending new org ID from localStorage:', pendingOrgId);
    }
  }, []);

  // Restore form state from localStorage on mount (EXCLUDING sensitive CMT member data)
  useEffect(() => {
    const savedForm = localStorage.getItem('onboardingFormState');
    if (savedForm) {
      try {
        const parsed = JSON.parse(savedForm);
        
        // SECURITY FIX: Never restore CMT member assignments from localStorage
        // This prevents data bleeding between organizations
        delete parsed.cmtMembers;
        
        console.log('[Onboarding] SECURITY: Removed CMT member data from localStorage restore');
        
        form.setFieldsValue(parsed);
        console.log('[Onboarding] Restored form state from localStorage (CMT data excluded):', parsed);
      } catch (e) {
        console.error('[Onboarding] Failed to parse saved form state:', e);
      }
    }
  }, [form]);

  // Function to clear all onboarding data and start fresh
  const startNewOnboarding = () => {
    // Clear all localStorage data
    localStorage.removeItem('onboardingFormState');
    localStorage.removeItem('pendingNewOrgId');
    localStorage.removeItem('tempOrgId');
    
    // Reset form
    form.resetFields();
    
    // Reset all state
    setSelectedOrgId(null);
    setTempOrgId(null);
    setCurrentStep(0);
    
    console.log('[Onboarding] Started fresh onboarding - all data cleared');
  };

  // Save form state to localStorage after every change (EXCLUDING sensitive CMT data)
  useEffect(() => {
    const handler = () => {
      const values = form.getFieldsValue(true);
      
      // SECURITY FIX: Never save CMT member data to localStorage
      const sanitizedValues = { ...values };
      delete sanitizedValues.cmtMembers;
      
      localStorage.setItem('onboardingFormState', JSON.stringify(sanitizedValues));
    };
    form.subscribe?.(handler);
    return () => {
      form.unsubscribe?.(handler);
    };
  }, [form]);

  // --- CONSOLIDATED ORG ID RECOVERY LOGIC ---
  useEffect(() => {
    // On mount, always try to recover org ID from localStorage if not in state
    if (!selectedOrgId) {
      const recoveredOrgId = localStorage.getItem('pendingNewOrgId');
      if (recoveredOrgId) {
        setSelectedOrgId(recoveredOrgId);
        console.log('[Onboarding] useEffect: recovered selectedOrgId from localStorage:', recoveredOrgId);
      }
    }
  }, []); // Only run on mount

  const steps = [
    {
      title: 'Organisation Info',
      content: <OrganisationInfo form={form} />,
    },
    {
      title: 'CMT Structure',
      content: <MultiCMTConfiguration form={form} />,
    },
    {
      title: 'Crisis Management Team',
      content: <CrisisManagementTeam form={form} />,
    },
    {
      title: 'Regulatory Rules',
      content: <RegulatoryRules form={form} />,
    },
    {
      title: 'Notification Preferences',
      content: <NotificationPreferences form={form} />,
    },
    {
      title: 'Templates and Documents',
      content: <TemplatesAndDocuments form={form} />,
    },
    // Only show site upload for super admins
    ...(currentUser?.isSuperAdmin ? [{
      title: 'Site Upload (Optional)',
      content: <SiteUpload 
        orgId={selectedOrgId || tempOrgId} 
        orgName={allFormValues.name || 'New Organisation'}
        onSitesUploaded={(results) => {
          console.log('Sites uploaded during onboarding:', results);
          message.success(`Uploaded ${results.successful} sites successfully`);
        }}
      />,
    }] : []),
  ];

  const getCurrentOrgId = async () => {
    console.log('getCurrentOrgId called');
    console.log('Current state:', { selectedOrgId, tempOrgId });
    
    if (selectedOrgId) {
      console.log('Using selectedOrgId:', selectedOrgId);
      return selectedOrgId;
    }
    if (tempOrgId) {
      console.log('Using tempOrgId:', tempOrgId);
      return tempOrgId;
    }
    
    // Try to recover from localStorage
    const storedTempId = localStorage.getItem('tempOrgId');
    console.log('Stored temp ID from localStorage:', storedTempId);
    if (storedTempId) {
      setTempOrgId(storedTempId);
      return storedTempId;
    }
    
    // If nothing exists, create a new temp org
    console.log('No existing org ID found, creating new temp org');
    return await ensureTempOrg();
  };

  const ensureTempOrg = async () => {
    try {
      console.log('Creating new temp org...');
      const tempOrgRef = collection(db, 'c3_organisations');
      const newTempOrg = {
        name: 'Temporary Organisation',
        isTemporary: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: currentUser.uid,
        updatedBy: currentUser.uid,
        clientStatus: 'pending'
      };
      
      console.log('Creating temp org with data:', newTempOrg);
      const docRef = await addDoc(tempOrgRef, newTempOrg);
      console.log('Temp org created with ID:', docRef.id);
      
      setTempOrgId(docRef.id);
      localStorage.setItem('tempOrgId', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating temp org:', error);
              throw new Error(`Failed to create temporary organisation: ${error.message}`);
    }
  };

  const handleNext = async () => {
    try {
      setIsTransitioning(true);
      await form.validateFields();
      const currentValues = form.getFieldsValue(true);
      
      // Clean client documents to prevent File object serialization errors
      if (!Array.isArray(currentValues.clientDocuments)) {
        currentValues.clientDocuments = [];
      }
      currentValues.clientDocuments = cleanClientDocuments(currentValues.clientDocuments);
      
      const orgIdToUse = await getCurrentOrgId();
      
      // Save the current step's data
      console.log('Saving step data:', { step: currentStep, values: currentValues });
      await setDoc(doc(db, 'c3_organisations', orgIdToUse, 'onboarding', `step_${currentStep}`), currentValues);
      
      if (currentStep === 0 && isNewOrg) {
        // Update the temp org with real values
        const orgRef = doc(db, 'c3_organisations', orgIdToUse);
        await updateDoc(orgRef, {
          ...currentValues,
          isTemporary: false
        });
        setSelectedOrgId(orgIdToUse);
        localStorage.setItem('pendingNewOrgId', orgIdToUse);
        localStorage.removeItem('tempOrgId');
        
        await logActivity({
          type: ACTIVITY_TYPES.DOCUMENT_CREATED,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          organisationId: orgIdToUse,
          details: `Created new organisation: ${currentValues.name || 'Unnamed Organisation'}`,
          metadata: {
            orgId: orgIdToUse,
            orgName: currentValues.name,
            subscriptionPlan: currentValues.subscriptionPlan,
            industry: currentValues.industry
          }
        });
      }
      
      setAllFormValues(prev => ({ ...prev, ...currentValues }));
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error('Validation failed:', error);
      if (error.code === 'permission-denied') {
        message.error('Permission denied. Please contact your administrator.');
      } else if (error.message?.includes('File object')) {
        message.error('File upload error. Please remove any problematic files and try again.');
      } else if (error.message?.includes('network') || error.code === 'unavailable') {
        message.error('Network error. Please check your connection and try again.');
      } else {
        message.error('Unable to proceed to next step. Please check your entries and try again.');
      }
    } finally {
      setIsTransitioning(false);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const cleanClientDocuments = (data) => {
    if (!Array.isArray(data)) return [];
    return data
      .filter(f => f && typeof f === 'object' && typeof f.name === 'string' && f.name.trim() && typeof f.url === 'string' && f.url.trim())
      .map(f => ({
        name: f.name,
        url: f.url,
      }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      if (!Array.isArray(values.clientDocuments)) {
        values.clientDocuments = [];
      }
      if (!Array.isArray(values.additionalLocations)) {
        values.additionalLocations = [];
      }
      values.clientDocuments = cleanClientDocuments(values.clientDocuments);
      localStorage.setItem('onboardingFormState', JSON.stringify(values));
      
      const orgIdToUse = await getCurrentOrgId();
      await setDoc(doc(db, 'c3_organisations', orgIdToUse, 'onboarding', `step_${currentStep}`), values);
      message.success('Progress saved successfully');
    } catch (error) {
      message.error('Failed to save progress');
    } finally {
      setLoading(false);
    }
  };

  const handleFinish = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue(true);
      console.log('Form values:', values);
      
      const orgIdToUse = await getCurrentOrgId();
      console.log('Using org ID:', orgIdToUse);

      // Get all onboarding data from subcollection
      const onboardingRef = collection(db, `c3_organisations/${orgIdToUse}/onboarding`);
      const onboardingSnapshot = await getDocs(onboardingRef);
      console.log('Onboarding snapshot:', onboardingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      // Merge all onboarding data
      const onboardingData = {};
      onboardingSnapshot.forEach(doc => {
        const stepData = doc.data();
        console.log('Merging step data:', { step: doc.id, data: stepData });
        Object.assign(onboardingData, stepData);
      });
      console.log('Merged onboarding data:', onboardingData);

      // Merge all data into a single object
      const orgUpdateFields = {
        ...onboardingData,  // Include all onboarding data
        name: values.name || onboardingData.name || '',
        subscriptionPlan: values.subscriptionPlan || onboardingData.subscriptionPlan || '',
        industry: values.industry || onboardingData.industry || '',
        description: values.description || onboardingData.description || '',
        abn: values.abn || onboardingData.abn || '',
        primaryAddress: values.primaryAddress || onboardingData.primaryAddress || '',
        timezone: values.timezone || onboardingData.timezone || '',
        cmtMembers: values.cmtMembers || onboardingData.cmtMembers || [],
        digitalProducts: values.digitalProducts || onboardingData.digitalProducts || [],
        onboardingComplete: true,
        onboardingCompletedAt: new Date(),
        isTemporary: false,
        clientStatus: 'active',
        createdAt: new Date(),
        createdBy: currentUser.uid,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
        _refreshTimestamp: new Date().getTime()
      };
      console.log('Final org update fields:', orgUpdateFields);

      // Update the main organisation document
      const orgRef = doc(db, 'c3_organisations', orgIdToUse);
      console.log('Updating org document at:', orgRef.path);
      
      try {
        // Use setDoc instead of updateDoc to ensure all fields are set
        await setDoc(orgRef, orgUpdateFields);
        console.log('Org document updated successfully');
        
        // Verify the document was created
        const verifyDoc = await getDoc(orgRef);
        console.log('Verification - Document exists:', verifyDoc.exists());
        console.log('Verification - Document data:', verifyDoc.data());

        // Create CMTs from hierarchy data if multi-CMT setup
        if (values.cmtStructureType === 'multi' && values.hierarchyData) {
          console.log('Creating CMTs from hierarchy data...');
          await createCMTsFromHierarchy(orgIdToUse, values.hierarchyData, currentUser);
          console.log('CMTs created successfully');
        }

        // 🚨 PHASE 2: CREATE USER DOCUMENTS FROM CMT MEMBER DATA
        console.log('🔧 ONBOARDING: Starting user creation from CMT member data...');
        const userCreationResults = await createUsersFromCMTMembers(orgIdToUse, values, currentUser);
        console.log('✅ ONBOARDING: User creation completed', userCreationResults);

        // 🚨 PHASE 2: COMPREHENSIVE USER FEEDBACK AND ERROR REPORTING
        if (userCreationResults.success) {
          if (userCreationResults.totalMembers > 0) {
            const { created, updated, totalMembers, errors } = userCreationResults;
            
            if (errors.length === 0) {
              // Perfect success - all users created/updated
              message.success(
                `✅ All ${totalMembers} team members processed successfully! ` +
                `(${created} new users created, ${updated} existing users updated)`
              );
            } else {
              // Partial success - some users failed
              const successCount = created + updated;
              message.warning(
                `⚠️ ${successCount}/${totalMembers} team members processed successfully. ` +
                `${errors.length} users had issues - check console for details.`
              );
              
              // Log detailed error information for troubleshooting
              console.warn('⚠️ USER CREATION PARTIAL FAILURES:', {
                successfulUsers: userCreationResults.successfulUsers,
                errors: errors
              });
            }
          } else {
            // No users to create
            console.log('ℹ️ No team members were configured for user creation');
          }
        } else {
          // Complete failure
          message.error(
            `❌ User creation failed: ${userCreationResults.errors?.[0]?.error || 'Unknown error'}. ` +
            `Organization created successfully, but team members will need to be added manually.`
          );
          
          console.error('❌ USER CREATION COMPLETE FAILURE:', userCreationResults);
        }

        // Log the activity
        await logActivity({
          type: ACTIVITY_TYPES.DOCUMENT_CREATED,
          userId: currentUser.uid,
          userEmail: currentUser.email,
          organisationId: orgIdToUse,
          details: `Created new organisation: ${values.name || 'Unnamed Organisation'}`,
          metadata: {
            orgId: orgIdToUse,
            orgName: values.name,
            subscriptionPlan: values.subscriptionPlan,
            industry: values.industry
          }
        });
      } catch (error) {
        console.error('Error updating org document:', error);
        throw new Error(`Failed to update organisation document: ${error.message}`);
      }

      // Set up digital product access
      if (values.digitalProducts && values.digitalProducts.length > 0) {
        const productIds = Array.isArray(values.digitalProducts) 
          ? values.digitalProducts 
          : [values.digitalProducts];
        
        console.log('Setting up products with IDs:', productIds);
        try {
          await setupOrganisationProducts(db, orgIdToUse, values.subscriptionPlan, productIds, currentUser);
          console.log('Product setup completed');
        } catch (error) {
          console.error('Error setting up products:', error);
          throw new Error(`Failed to set up organisation products: ${error.message}`);
        }
      }

      try {
        await createDefaultPaths(orgIdToUse);
        console.log('Default paths created');
      } catch (error) {
        console.error('Error creating default paths:', error);
        throw new Error(`Failed to create default paths: ${error.message}`);
      }
      
      // Clean up
      localStorage.removeItem('pendingNewOrgId');
      localStorage.removeItem('tempOrgId');
      localStorage.removeItem('onboardingFormState');
      console.log('Local storage cleaned up');
      
      message.success('Onboarding completed successfully!');
      
      // Navigate to admin page with clients tab active and force refresh
      navigate('/admin?tab=clients', { 
        state: { 
          activeTab: 'clients',
          forceRefresh: true 
        },
        replace: true
      });
    } catch (error) {
      console.error('Error completing onboarding:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        code: error.code
      });
      message.error(`Failed to complete onboarding: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultPaths = async (orgId) => {
    try {
      if (!orgId) {
        console.error('OnboardingWizard: orgId is null or undefined in createDefaultPaths!');
        return;
      }
      // Create default collections
      const defaultCollections = [
        'c3_incidents',
        'c3_milestones',
        'c3_comm_groups'
      ];

      for (const collection of defaultCollections) {
        await setDoc(doc(db, collection, orgId), {
          createdAt: new Date(),
          createdBy: currentUser.uid,
        });
      }
    } catch (error) {
      console.error('Error creating default paths:', error);
      throw error;
    }
  };

  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      // Only clean up if it's still a temporary org
      if (tempOrgId && !selectedOrgId) {
        const checkAndCleanup = async () => {
          try {
            const orgRef = doc(db, 'c3_organisations', tempOrgId);
            const orgDoc = await getDoc(orgRef);
            
            // Only delete if it's still a temporary org
            if (orgDoc.exists() && orgDoc.data().isTemporary) {
              await deleteDoc(orgRef);
              localStorage.removeItem('tempOrgId');
            }
          } catch (error) {
            console.error('Error during cleanup:', error);
          }
        };
        
        checkAndCleanup();
      }
    };
  }, [tempOrgId, selectedOrgId, db]);

  return (
    <div className="p-4">
      <PageHeader title="Client Onboarding" />
      
      <Form form={form} layout="vertical">
        <Card className="mb-4">
          <Steps current={currentStep} className="mb-8">
            {steps.map(item => (
              <Step key={item.title} title={item.title} />
            ))}
          </Steps>

          <div className="steps-content">
            {steps[currentStep].content}
          </div>

          <div className="steps-action mt-8 flex justify-between">
            {currentStep > 0 && (
              <Button onClick={handlePrev}>
                Previous
              </Button>
            )}
            <div className="flex gap-2">
              <Button onClick={handleSave} loading={loading}>
                Save Progress
              </Button>
              {currentStep < steps.length - 1 && (
                <Button type="primary" onClick={handleNext} loading={isTransitioning}>
                  Next
                </Button>
              )}
              {currentStep === steps.length - 1 && (
                <Button type="primary" onClick={handleFinish} loading={loading}>
                  Complete Setup
                </Button>
              )}
            </div>
          </div>
        </Card>
      </Form>
    </div>
  );
} 